import { useAuthOptional } from "@/hooks/useAuth";
import { invalidate } from "@/lib/mutation-invalidation";
import {
  focusManager,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type RefreshReason = "resume" | "focus" | "visibility" | "bfcache" | "online";

type AppLifecycleContextValue = {
  isRefreshing: boolean;
  lastRefreshAt: number | null;
  lastReason: string | null;
  refreshCritical: (reason?: string) => void;
};

const AppLifecycleContext = createContext<AppLifecycleContextValue | undefined>(
  undefined
);

function getNow(): number {
  return Date.now();
}

function isStableNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/** Begrenzt parallele Supabase-Calls beim Resume (jeder ruft intern getSession auf). */
async function refetchActiveQueriesBatched(
  queryClient: QueryClient,
  batchSize: number,
  batchDelayMs: number
): Promise<void> {
  const active = queryClient
    .getQueryCache()
    .getAll()
    .filter(q => q.isActive() && q.state.fetchStatus !== "fetching");

  for (let i = 0; i < active.length; i += batchSize) {
    const batch = active.slice(i, i + batchSize);
    await Promise.all(
      batch.map(q =>
        q.fetch().catch(() => {
          /* Einzelner Refetch-Fehler blockiert nicht den gesamten Zyklus */
        })
      )
    );
    if (i + batchSize < active.length && batchDelayMs > 0) {
      await new Promise<void>(resolve => setTimeout(resolve, batchDelayMs));
    }
  }
}

/**
 * Single Source of Truth für App-Lifecycle (Resume/Online/Focus/BFCache).
 *
 * Regeln/Vertrag:
 * - Resume Detection nur hier
 * - Auth Gate: Refresh erst wenn `authReady && userId` stabil sind
 * - Refresh Plan: invalidate("all") + React Query Refresh
 * - Throttling/Dedup: keine Refetch-Stürme
 * - Status: isRefreshing/lastRefreshAt/lastReason
 */
export function AppLifecycleProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const queryClient = useQueryClient();
  const auth = useAuthOptional();

  const authReady = Boolean(auth && auth.isLoading === false);
  const userId = auth?.user?.id ?? null;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [lastReason, setLastReason] = useState<string | null>(null);

  const lastRefreshAtRef = useRef<number>(0);
  const isRefreshingRef = useRef(false);
  const pendingReasonRef = useRef<string | null>(null);
  const stableUserIdRef = useRef<string | null>(null);
  const stableUserIdSinceRef = useRef<number>(0);
  const appStateListenerRef = useRef<{ remove: () => void } | null>(null);
  const resumeDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Trackt wann der Tab zuletzt unsichtbar wurde – für Focus-Schwellenwert
  const hiddenSinceRef = useRef<number>(0);
  const lastCapacitorResumeAtRef = useRef<number>(0);
  const lastVisibilityResumeAtRef = useRef<number>(0);

  const THROTTLE_MS = 10_000;
  const USERID_STABLE_MS = 250;
  const RESUME_DEBOUNCE_MS = 300; // Coalesce visibility + focus Events
  // Capacitor resume + visibilitychange kurz danach → nur ein Refresh
  const CAPACITOR_VISIBILITY_COALESCE_MS = 2_000;
  // focus-Events lösen Refresh nur aus wenn Tab mindestens so lange hidden war
  const FOCUS_MIN_HIDDEN_MS = 30_000;
  const REFETCH_BATCH_SIZE = 4;
  const REFETCH_BATCH_DELAY_MS = 60;

  // Track "stable" userId (Schutz gegen transient null beim Resume)
  useEffect(() => {
    const now = getNow();
    if (isStableNonEmptyString(userId)) {
      if (stableUserIdRef.current !== userId) {
        stableUserIdRef.current = userId;
        stableUserIdSinceRef.current = now;
      }
      return;
    }
    // userId ist null/leer -> reset stability window
    stableUserIdRef.current = null;
    stableUserIdSinceRef.current = 0;
  }, [userId]);

  const canRefreshNow = useCallback((): boolean => {
    if (!authReady) return false;
    if (!isStableNonEmptyString(stableUserIdRef.current)) return false;
    const since = stableUserIdSinceRef.current;
    if (!since) return false;
    return getNow() - since >= USERID_STABLE_MS;
  }, [authReady]);

  const doRefresh = useCallback(
    async (reason: string) => {
      const now = getNow();
      const isExplicitPullRefresh = reason === "pull";

      if (isRefreshingRef.current) return;
      // Pull-to-Refresh ist bewusste Nutzeraktion — nicht durch Resume-Throttle blockieren.
      if (
        !isExplicitPullRefresh &&
        now - lastRefreshAtRef.current < THROTTLE_MS
      ) {
        return;
      }

      if (!canRefreshNow()) {
        pendingReasonRef.current = reason;
        return;
      }

      const stableUserId = stableUserIdRef.current;
      console.log("[Lifecycle] authReady userId=" + stableUserId);
      console.log("[Lifecycle] refresh start reason=" + reason);

      isRefreshingRef.current = true;
      setIsRefreshing(true);
      setLastReason(reason);

      try {
        // ⚠️ RESUME-BUG FIX (2026-02-10): KEIN getSession() hier!
        // getSession() bei Resume → Supabase refresht Token → feuert SIGNED_IN →
        // useAuth.tsx ruft fetchSubscription() auf → Request-Storm →
        // refetchQueries() hängt endlos → "react-query refresh done" kommt nie →
        // Dashboard-Daten (Figuren, Zahlen, Events) bleiben leer.
        // Supabase handelt Token-Refresh automatisch bei jedem API-Call.
        // Siehe: docs/session-notes/2026-02-10-resume-bug-root-cause-fix.md

        if (!navigator.onLine) {
          console.log("[Lifecycle] offline - skip refresh");
        }

        // TanStack Query Focus-Detection: Override entfernen, damit die natürliche
        // Browser-basierte Detection (document.visibilityState) greift.
        // WICHTIG: setFocused(true) darf NICHT dauerhaft gesetzt werden, weil
        // TanStack Query nur bei VALUE-CHANGE ein Refetch triggert.
        // Bei permanentem Override (true->true) gibt es KEIN Change-Event!
        focusManager.setFocused(undefined);

        invalidate("all");
        console.log("[Lifecycle] invalidate(all) fired");

        // ⚠️ RESUME-BUG FIX (2026-02-10): refetchType: 'none' ist PFLICHT!
        // Ohne 'none' startet invalidateQueries() eigene Refetches, die parallel
        // zu refetchQueries() (Schritt 2) laufen → doppelte Requests, Race-Conditions.
        // Schritt 1: Alle Queries als "stale" markieren (OHNE automatischen Refetch).
        try {
          await queryClient.invalidateQueries({ refetchType: "none" });
          console.log("[Lifecycle] invalidated ALL queries (stale-marked)");
        } catch (invalidateError) {
          console.warn(
            "[Lifecycle] invalidateQueries error (non-fatal):",
            invalidateError
          );
        }

        // ⚠️ RESUME-BUG FIX (2026-02-10): Timeout ist PFLICHT!
        // Schritt 2: Aktive Queries refetchen (single controlled refetch cycle).
        // Ohne Timeout kann refetchQueries() endlos hängen, wenn Auth-Events
        // (SIGNED_IN durch Token-Refresh) neue Queries auslösen, die wiederum
        // in die refetch-Queue eingehen → Endlosschleife.
        // Nach REFETCH_TIMEOUT_MS geben wir auf; Queries laden im Hintergrund weiter.
        const REFETCH_TIMEOUT_MS = 2_500;
        try {
          const refetchDone = refetchActiveQueriesBatched(
            queryClient,
            REFETCH_BATCH_SIZE,
            REFETCH_BATCH_DELAY_MS
          ).then(() => true);
          const timeout = new Promise<boolean>(resolve =>
            setTimeout(() => resolve(false), REFETCH_TIMEOUT_MS)
          );
          const completed = await Promise.race([refetchDone, timeout]);
          if (completed) {
            console.log("[Lifecycle] react-query refresh done");
          } else {
            console.log(
              "[Lifecycle] react-query refresh timeout after " +
                REFETCH_TIMEOUT_MS +
                "ms (queries loading in background)"
            );
          }
        } catch (refetchError) {
          // Fehler beim Refetch: Loggen, aber nicht weiterwerfen
          // UI sollte mit placeholderData weiter funktionieren
          console.warn(
            "[Lifecycle] refetchQueries error (non-fatal):",
            refetchError
          );
        }

        lastRefreshAtRef.current = now;
        setLastRefreshAt(now);
      } finally {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
      }
    },
    [canRefreshNow, queryClient]
  );

  const refreshCritical = useCallback(
    (reason?: string) => {
      void doRefresh(reason || "manual");
    },
    [doRefresh]
  );

  // Wenn auth/userId später "stabil" werden, Pending-Refresh nachholen
  useEffect(() => {
    if (!pendingReasonRef.current) return;
    if (!canRefreshNow()) return;
    const reason = pendingReasonRef.current;
    pendingReasonRef.current = null;
    void doRefresh(reason);
  }, [canRefreshNow, doRefresh, authReady, userId]);

  // Resume Detection: Capacitor App Events + Browser Events (visibility/focus/online/pageshow)
  // ⚠️ RESUME-BUG FIX (2026-02-10): DEBOUNCE ist PFLICHT!
  // Chrome feuert bei Tab-Rückkehr ZWEI Events gleichzeitig: visibilitychange + focus.
  // Ohne Debounce starten zwei parallele doRefresh()-Aufrufe → der zweite wird zwar
  // durch isRefreshingRef geblockt, aber das Timing kann trotzdem zu Race-Conditions führen.
  // Siehe: docs/session-notes/2026-02-10-resume-bug-root-cause-fix.md
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Debounced Resume Handler: Coalesce mehrere Events innerhalb RESUME_DEBOUNCE_MS
    const onResumeDetected = (reason: RefreshReason) => {
      console.log("[Lifecycle] resume detected reason=" + reason);

      if (
        reason === "visibility" &&
        lastCapacitorResumeAtRef.current > 0 &&
        getNow() - lastCapacitorResumeAtRef.current <
          CAPACITOR_VISIBILITY_COALESCE_MS
      ) {
        return;
      }

      if (
        reason === "focus" &&
        lastVisibilityResumeAtRef.current > 0 &&
        getNow() - lastVisibilityResumeAtRef.current <
          CAPACITOR_VISIBILITY_COALESCE_MS
      ) {
        return;
      }

      // Online/BFCache Events sofort verarbeiten (kein Debounce nötig)
      if (reason === "online" || reason === "bfcache" || reason === "resume") {
        if (reason === "resume") {
          lastCapacitorResumeAtRef.current = getNow();
        }
        void doRefresh(reason);
        return;
      }

      // Visibility + Focus Events debounce: Nur das erste Event innerhalb 300ms verarbeiten
      if (resumeDebounceTimerRef.current) {
        // Timer läuft bereits → dieses Event ignorieren (wird vom ersten abgedeckt)
        return;
      }

      resumeDebounceTimerRef.current = setTimeout(() => {
        resumeDebounceTimerRef.current = null;
      }, RESUME_DEBOUNCE_MS);

      if (reason === "visibility") {
        lastVisibilityResumeAtRef.current = getNow();
      }

      void doRefresh(reason);
    };

    // Capacitor App Lifecycle Events (zuverlässiger als Browser-Events in WebView)
    let disposed = false;
    void (async () => {
      try {
        if (typeof window !== "undefined" && (window as any).Capacitor) {
          const { App } = await import("@capacitor/app");
          if (disposed) return;
          if (App && typeof App.addListener === "function") {
            const listener = await App.addListener(
              "appStateChange",
              ({ isActive }) => {
                if (isActive) {
                  console.log(
                    "[Lifecycle] Capacitor appStateChange: active (resume)"
                  );
                  onResumeDetected("resume");
                } else {
                  console.log(
                    "[Lifecycle] Capacitor appStateChange: inactive (pause)"
                  );
                }
              }
            );
            if (disposed) {
              listener.remove();
              return;
            }
            appStateListenerRef.current = listener;
            console.log("[Lifecycle] Capacitor App listener registered");
          }
        }
      } catch {
        console.log(
          "[Lifecycle] Capacitor App plugin not available, using browser events only"
        );
      }
    })();

    // Browser Events als Fallback (für PWA/Web)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onResumeDetected("visibility");
      } else {
        // Tab wird versteckt: Zeitstempel merken (für Focus-Schwellenwert)
        hiddenSinceRef.current = getNow();
      }
    };

    const handleFocus = () => {
      // Erster Focus nach Mount: Tab war nie hidden → kein Resume-Refresh.
      if (hiddenSinceRef.current === 0) return;
      const hiddenDuration = getNow() - hiddenSinceRef.current;
      if (hiddenDuration < FOCUS_MIN_HIDDEN_MS) return;
      onResumeDetected("focus");
    };
    const handleOnline = () => onResumeDetected("online");
    const handlePageShow = (e: PageTransitionEvent) => {
      const persisted = (e as unknown as { persisted?: boolean }).persisted;
      if (persisted) onResumeDetected("bfcache");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      disposed = true;
      if (resumeDebounceTimerRef.current) {
        clearTimeout(resumeDebounceTimerRef.current);
        resumeDebounceTimerRef.current = null;
      }
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
        appStateListenerRef.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [doRefresh]);

  const value = useMemo<AppLifecycleContextValue>(
    () => ({
      isRefreshing,
      lastRefreshAt,
      lastReason,
      refreshCritical,
    }),
    [isRefreshing, lastRefreshAt, lastReason, refreshCritical]
  );

  return (
    <AppLifecycleContext.Provider value={value}>
      {children}
    </AppLifecycleContext.Provider>
  );
}

export function useAppLifecycle(): AppLifecycleContextValue {
  const ctx = useContext(AppLifecycleContext);
  if (!ctx) {
    throw new Error(
      "useAppLifecycle muss innerhalb von AppLifecycleProvider sein"
    );
  }
  return ctx;
}
